import { Button } from "components/ui/button";
import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { Color } from "utils/theme";
import { MinusIcon, PlusIcon } from "lucide-react";

type BrightnessSliderProps = {
  horizontal?: boolean;
};

export const BrightnessSlider = ({
  horizontal = false,
}: BrightnessSliderProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState("");
  const [value, setValue] = useState(50);
  const min = 5;
  const max = 100;
  const step = 5;

  useEffect(() => {
    // initialize brightness value
    getScreenBrightness();
  }, []);

  const getScreenBrightness = async () => {
    try {
      const hostname = window.location.hostname;
      const url = `http://${hostname}/beijer_api/getbrightness`;
      const response = await fetch(url);
      const text = await response.text();
      const parsedValue = Number(text.trim());
      if (!isNaN(parsedValue)) {
        setValue(parsedValue);
      } else {
        setError(`Invalid brightness value received: "${text}"`);
      }
    } catch (error: any) {
      console.error("Error: ", error);
      setError(`Error getting brightness value ${error.toString()}`);
    }
  };

  const setScreenBrightness = useCallback(async (value: number) => {
    try {
      const hostname = window.location.hostname;
      const url = `http://${hostname}/beijer_api/setbrightness?value=${value}`;
      await fetch(url, { method: "GET" });
    } catch (error: any) {
      console.error("Error: ", error);
      setError(`Error setting brightness value ${error.toString()}`);
    }
  }, []);

  // Handle slider change with debounce
  const handleChange = (value: number) => {
    setValue(value);
    setScreenBrightness(value);
  };

  return (
    <div
      className={classNames(
        "relative flex items-center rounded-md overflow-hidden border",
        horizontal
          ? "w-full flex-row"
          : "h-full flex-col-reverse w-12 overflow-hidden"
      )}
      style={{
        backgroundColor: Color.Primary[700],
        borderColor: Color.Primary[600],
      }}
    >
      <Button
        className={classNames(
          "rounded-none border-0",
          horizontal ? "w-12 h-12" : "w-12 h-16"
        )}
        onClick={() => handleChange(Math.max(value - 5, min))}
        size="icon"
        variant="secondary"
      >
        <MinusIcon className="size-6" />
      </Button>
      <div
        className={classNames(
          "relative flex items-center",
          horizontal ? "w-full h-12" : "h-full w-12 flex-col"
        )}
      >
        {/* Filled Progress Track */}
        <div
          className={classNames(
            "absolute z-10 opacity-60 rounded-lg",
            horizontal ? "h-9" : "w-10"
          )}
          style={
            horizontal
              ? {
                  width: `${((value - min) / (max - min)) * 100}%`,
                  background: Color.White,
                }
              : {
                  height: `${((value - min) / (max - min)) * 100}%`,
                  bottom: 0,
                  background: Color.White,
                }
          }
        />

        {/* Tick Marks */}
        {Array.from({ length: (max - min) / step + 1 }, (_, i) => {
          const tickValue = min + i * step;
          return (
            <div
              key={tickValue}
              className="absolute z-0"
              style={
                horizontal
                  ? {
                      left: `${((tickValue - min) / (max - min)) * 100}%`,
                      width: "2px",
                      height: "1.5rem",
                      background: Color.Primary[700],
                    }
                  : {
                      bottom: `${((tickValue - min) / (max - min)) * 100}%`,
                      width: "1.5rem",
                      height: "2px",
                      background: Color.Primary[700],
                    }
              }
            />
          );
        })}

        {/* Hidden Range Input */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => handleChange(Number(e.target.value))}
          className="absolute opacity-0 cursor-pointer z-20"
          aria-orientation="vertical"
          style={
            horizontal
              ? { width: "100%", height: "100%" }
              : {
                  height: "100%",
                  width: "100%",
                  writingMode: "vertical-lr", // Standard for vertical sliders
                  direction: "rtl", // Ensures min value is at bottom
                  verticalAlign: "middle",
                  WebkitAppearance: "slider-vertical" as any, // Fix for Safari
                  MozAppearance: "slider-vertical" as any, // Fix for Firefox
                  appearance: "slider-vertical" as any, // General Fix
                }
          }
        />

        {/* Indicator Handle */}
        {/* <div
          className="absolute"
          style={
            horizontal
              ? {
                  left: `${((value - min) / (max - min)) * 100}%`,
                  width: "2px",
                  height: "100%",
                  transform: "translateX(-50%)",
                  background: Color.White,
                }
              : {
                  bottom: `${((value - min) / (max - min)) * 100}%`,
                  width: "100%",
                  height: "2px",
                  transform: "translateY(50%)",
                  background: Color.White,
                }
          }
        /> */}
      </div>
      <Button
        className={classNames(
          "rounded-none border-0",
          horizontal ? "w-12 h-12" : "w-12 h-16"
        )}
        variant="secondary"
        size="icon"
        onClick={() => handleChange(Math.min(value + 5, max))}
      >
        <PlusIcon className="size-6" />
      </Button>
    </div>
  );
};
